import type { VisibleRangeProfileBucket } from '@/features/chart/lib/visible-range-profile'
import type { LevelProfileMeasure } from '@/types/dashboard'
import { Exchange } from '@/types/generated/common/types'
import type { LiquidationLevelProfile, StopLevelProfile } from '@/types/generated/public_api'

// Venues whose producer publishes liquidation & stop-level profiles (oems #261) — perps only.
export const LIQUIDATION_LEVEL_PROFILE_EXCHANGES: ReadonlySet<Exchange> = new Set([
  Exchange.EXCHANGE_HYPERLIQUID,
])

function toFiniteNumber(value: number | string): number {
  const n = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(n) ? n : 0
}

function buildBuckets(
  priceMin: number,
  bucketWidth: number,
  positiveRaw: readonly (number | string)[],
  negativeRaw: readonly (number | string)[]
): VisibleRangeProfileBucket[] {
  // The producer always snaps bucketWidth to a positive round step, but the UI shouldn't fall
  // over if that's ever violated — a non-positive width can't place buckets meaningfully.
  if (!(bucketWidth > 0)) return []

  const length = Math.min(positiveRaw.length, negativeRaw.length)
  return Array.from({ length }, (_, index) => {
    const low = priceMin + index * bucketWidth
    const high = low + bucketWidth
    return {
      index,
      low,
      high,
      mid: low + bucketWidth / 2,
      positive: toFiniteNumber(positiveRaw[index]),
      negative: toFiniteNumber(negativeRaw[index]),
    }
  })
}

// positive = long (liquidates below mid), negative = short (liquidates above mid) — a spatial
// split by position direction, not an arbitrary buy/sell convention.
export function selectLiquidationLevelBuckets(
  profile: LiquidationLevelProfile,
  measure: LevelProfileMeasure
): VisibleRangeProfileBucket[] {
  const [positiveRaw, negativeRaw] =
    measure === 'count'
      ? [profile.longLiquidationLevelCount, profile.shortLiquidationLevelCount]
      : [profile.longLiquidationLevelNotional, profile.shortLiquidationLevelNotional]

  return buildBuckets(profile.priceMin, profile.bucketWidth, positiveRaw, negativeRaw)
}

// positive = take-profit, negative = stop-loss. These are alternative outcomes for one position,
// never summed — a position with both attached rests as two orders carrying the same notional,
// and only one can ever fire. Rendering them as separate positive/negative arrays (never added
// together) is what keeps that true; nothing downstream sums these two.
export function selectStopLevelBuckets(
  profile: StopLevelProfile,
  measure: LevelProfileMeasure
): VisibleRangeProfileBucket[] {
  const [positiveRaw, negativeRaw] =
    measure === 'count'
      ? [profile.takeProfitCount, profile.stopLossCount]
      : [profile.takeProfitNotional, profile.stopLossNotional]

  return buildBuckets(profile.priceMin, profile.bucketWidth, positiveRaw, negativeRaw)
}
